import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { useCmsCollection } from "../hooks/useCms";
import { ServiceCard } from "../components/ServiceCard";
import { CampaignBanner } from "../components/CampaignBanner";
import { GallerySection } from "../components/GallerySection";
import { BUSINESS_INFO } from "../config";

export function ServicesPage() {
  const { items: ydelser, loading } = useCmsCollection("ydelser", { sort: "sort_order" });
  const [filter, setFilter] = useState("alle");

  // Filter options
  const filterOptions = [
    { id: "alle", label: "Alle behandlinger" },
    { id: "klip", label: "Klip, vask & negle" },
    { id: "vask", label: "Vask & føn" },
    { id: "hvalp", label: "Hvalpepakke" },
    { id: "diverse", label: "Poter, negle & tillæg" },
  ];

  // Filtering logic
  const filteredServices = ydelser
    .filter((s) => s.is_active !== false)
    .filter((s) => {
      if (filter === "alle") return true;
      const title = s.title.toLowerCase();
      if (filter === "klip") return title.includes("klip, vask, føn samt negle");
      if (filter === "vask") return title.startsWith("vask, føn");
      if (filter === "hvalp") return title.includes("hvalpepakke");
      if (filter === "diverse") return title.includes("poter") || title.includes("tillæg");
      return true;
    });

  return (
    <>
      <SEO
        title="Ydelser & priser - Hundens Pels og Pleje"
        description="Ydelser & priser - Hundens Pels og Pleje. Se vores attraktive priser på klip, vask, føn og negleklip i Fredericia tilpasset alle hundestørrelser."
      />

      {/* Hero Header - No box behind heading and description */}
      <section className="relative bg-white pt-14 pb-24 sm:pt-20 sm:pb-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-black">
            Ydelser & priser
          </h1>

          <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-neutral-700 leading-relaxed">
            Herunder finder du en komplet oversigt over vores behandlinger. Vi tilpasser altid håndteringen og udstyret efter din hunds størrelse, race og temperament.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href={BUSINESS_INFO.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#dabeb9] px-7 py-3.5 text-sm font-medium tracking-wide text-black shadow-xs transition-colors hover:bg-[#c9a7a0]"
            >
              <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
              <span>Gå direkte til online booking</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-6 py-3.5 text-sm font-medium tracking-wide text-black transition-colors hover:bg-black hover:text-white whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-current" aria-hidden="true" />
              <span>Kontakt os ved spørgsmål</span>
            </Link>

            {/* Secondary CTA: NO background color and NO hover background color */}
            <a
              href={`tel:${BUSINESS_INFO.phoneRaw}`}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-transparent px-6 py-3.5 text-sm font-medium tracking-wide text-neutral-800 transition-colors hover:border-black hover:text-black whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-black" aria-hidden="true" />
              <span className="whitespace-nowrap">{BUSINESS_INFO.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Services Section with Filter tabs */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#FEFAF3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Important philosophy callout - clean text without box */}
          <div className="mb-10 text-center max-w-3xl mx-auto space-y-2">
            <p className="font-heading text-lg sm:text-xl font-medium text-black">
              Vigtigt princip: Hensynet til hunden vægtes altid højst
            </p>
            <p className="text-sm sm:text-base text-neutral-700 leading-relaxed">
              Vi tager altid udgangspunkt i dine ønsker, men presser aldrig hunden ud over dens grænser. Vi anvender milde og økologiske plejeprodukter tilpasset hunden til gavn for både hunde og naturen (herunder særlig hvalpeshampoo til de mindste). Oplys meget gerne hundens race ved tidsbestilling.
            </p>
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 pb-8">
            {filterOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFilter(opt.id)}
                className={`rounded-full px-5 py-2 text-xs sm:text-sm font-medium transition-all ${
                  filter === opt.id
                    ? "bg-black text-white shadow-xs"
                    : "bg-transparent text-black border border-black/30 hover:border-black hover:text-black"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Services Grid */}
          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="h-64 animate-pulse rounded-2xl bg-[#EAE1D7]" />
              ))}
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#DCCEC2] p-8 text-center text-sm text-black">
              Ingen behandlinger matcher det valgte filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredServices.map((service) => (
                <ServiceCard key={service.id || service.title} service={service} />
              ))}
            </div>
          )}

          {/* Kontakt opfordring for særlige behov / spørgsmål */}
          <div className="mt-14 rounded-3xl border border-[#E8DFD5] bg-white p-8 sm:p-10 text-center max-w-3xl mx-auto shadow-xs">
            <h3 className="font-heading text-xl sm:text-2xl font-medium text-black">
              Har din hund særlige behov eller er du i tvivl om racens krav?
            </h3>
            <p className="mt-3 text-sm sm:text-base text-neutral-700 leading-relaxed max-w-xl mx-auto">
              Alle hunde er unikke. Kontakt os gerne forud for dit besøg, hvis du har spørgsmål til filtrering, raceklip, adfærd eller særlige hensyn.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-[#dabeb9] px-7 py-3 text-sm font-medium tracking-wide text-black shadow-xs transition-colors hover:bg-[#c9a7a0]"
              >
                <i className="fa-solid fa-comment-dots text-black" aria-hidden="true" />
                <span>Kontakt salonen</span>
              </Link>
              <a
                href={`tel:${BUSINESS_INFO.phoneRaw}`}
                className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-6 py-3 text-sm font-medium tracking-wide text-black transition-colors hover:bg-black hover:text-white whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-current" aria-hidden="true" />
                <span>Ring {BUSINESS_INFO.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Åbningstilbud section */}
      <section className="py-12 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <CampaignBanner />
        </div>
      </section>

      {/* Relevant Gallery */}
      <GallerySection
        categorySlug={filter === "vask" ? "vask-foen" : filter === "hvalp" ? "hvalpe" : "klip"}
        title="Billeder fra salonens behandlinger"
        subtitle="Se eksempler på vores klipninger, vask og rolige behandlinger."
        showTabs={false}
      />
    </>
  );
}
